<template>
  <div>
    <tool-bar
        :title="toolbarTitle"
        :right-menu-actions="toolbarActionsList"
        :close-action-navigation-activity="closeActionNavigationActivity"
    ></tool-bar>

    <loading :loading="loading"></loading>

    <error-alert-item
        :error-alert-event="errorAlertEvent"
        :network-error="networkError"
        @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <update-identifier-verify-dialog
        ref="updateIdentifierVerifyDialog"
        :show="showUpdateIdentifierVerifyDialog"
        @onPhoneNumberVerified="onUpdateIdentifierDataEntered"
        @onDismiss="showUpdateIdentifierVerifyDialog=false"
    ></update-identifier-verify-dialog>

    <update-identifier-message-dialog
        :show="showUpdateIdentifierMessageDialog"
        :message="updateIdentifierMessage"
        button-text="Dismiss"
        @onContinue="showUpdateIdentifierMessageDialog = false"
    ></update-identifier-message-dialog>

    <consent-phone-number-verify-dialog
        :show="showConsentPhoneNumberVerifyDialog"
        ref="consentPhoneNumberVerifyDialog"
        @onPhoneNumberVerified="onConsentPhoneNumberVerified"
    ></consent-phone-number-verify-dialog>

    <date-picker-dialog
        select-date-title="Select Date"
        :show-date-picker="showDatePicker"
        :default-selected-date="defaultSelectedDate"
        :max-allowed-date="currentDateAsYYYYMMDD"
        @closeClicked="showDatePicker=false"
        @dateSelected="dobSelected"
    ></date-picker-dialog>

    <choose-client-identifier-document-item
        :show="showClientIdentifierOptions"
        class="margin-top-20"
        @enterDetailsManually="onEnterDetailsManually"
    ></choose-client-identifier-document-item>

    <div class="margin_top_20"
         v-if="showClientDetails && consentFetched && !showClientIdentifierOptions">

      <div class="input_container">
        <weex-text class="input_label">Document Type</weex-text>
        <input
            :disabled="true"
            class="input_with_border_bottom"
            :value="clientIdentifierDocumentName"
        />
      </div>

      <div class="input_container" v-if="isClientIdentifierMandatory">
        <weex-text class="input_label">{{ getIdentifierIdLabel }}</weex-text>
        <input
            class="input_with_border_bottom"
            :value="clientIdentifierDocumentId"
            @input="onClientIdentifierDocumentIdEntered($event.target.value)"
            :maxlength="getClientIdentifierInputMaxLength"
            auto-capitalization-type="allCharacters"
        />
      </div>

      <div class="input_container" v-if="!firstStage">
        <weex-text class="input_label">Full Name</weex-text>
        <weex-text class="input_label_des" v-if="isClientIdentifierDocumentPan">(Same as in PAN
          Card)
        </weex-text>
        <input
            class="input_with_border_bottom"
            :value="personFullName"
            @input="nameEntered($event.target.value)"
            auto-capitalization-type="words"
        />
      </div>

      <div class="input_container" v-if="!firstStage">
        <weex-text class="input_label">{{ getLabelForDateOfBirth }}</weex-text>
        <weex-text class="input_label_des" v-if="isClientIdentifierDocumentPan">(Same as in PAN
          Card)
        </weex-text>
        <input
            ref="dob"
            :value="dob"
            class="input_with_border_bottom"
            @focus="showDatePickerDialog"
        />
      </div>

      <div class="input_container"
           v-if="!firstStage && !isApplicantOrganization && !isOnboardingLoan">
        <weex-text class="input_label">Gender</weex-text>
        <wxc-radio
            :list="genderList"
            :config="radioButtonConfig"
            :radio-button-on-right="false"
            :has-bottom-border="false"
            @wxcRadioListChecked="genderSelected"
        ></wxc-radio>
      </div>

      <div class="margin-top-30 margin-left-20 margin-right-20"
           v-if="firstStage && !showClientIdentifierOptions">
        <weex-text
            v-if="consentHeading"
            class="consent-header"
            v-html="consentHeading"
        ></weex-text>
        <div
            class="item-row-alignment margin-top-10"
            v-for="(consent, index) in consents"
            :key="consent.consentValue + index">
          <weex-image
              :src="checkBoxLink(consent)"
              @click="onConsentSelected(consent)"
              class="ezcred-checkbox-image"
          ></weex-image>
          <weex-text
              class="ezcred-checkbox-text"
              v-html="consent.consentValue"
          ></weex-text>
        </div>
      </div>

      <wxc-button
          v-if="firstStage"
          text="Next"
          class="button_horizontal_center"
          style="margin-top: 30px"
          type="blue"
          @wxcButtonClicked="checkDedupe"
      >
      </wxc-button>

      <wxc-button
          v-if="!firstStage"
          text="Next"
          class="button_horizontal_center"
          style="margin-top: 30px"
          type="blue"
          @wxcButtonClicked="onNext"
      >
      </wxc-button>
    </div>
  </div>
</template>

<script>
import ToolBar from "../../layout/ToolBar";
import Loading from "../../layout/Loading";
import WxcRadio from "../../../../weex-ui/packages/wxc-radio/index";
import WxcButton from "../../../../weex-ui/packages/wxc-button/index";
import WxcDropdown from "../../../../weex-ui/packages/wxc-dropdown/index";
import AlertService from "../../core/alert/AlertService";
import ActivityNavigator, { Activities } from "../../../utils/ActivityNavigator";
import DataSource from "../../../data/DataSource";
import LoanTasksActivityNavigator from "../../../utils/LoanTasksActivityNavigator";
import EzUtils from "../../../utils/EzUtils";
import { DefaultResourceConfig, ImageResourcesUtil } from "../../../utils/ImageResourcesUtil";
import { MessageConstants } from "../../../utils/MessageConstantUtil";
import DatePickerDialog from "../../dialogs/DatePickerDialog";
import DateUtils, { DateFormats } from "../../../utils/DateUtils";
import ChooseClientIdentifierDocumentItem
  from "../../layout/ChooseClientIdentifierDocumentItem.vue";
import ErrorAlertItem from "../../layout/ErrorAlertItem";
import PrefManager from "../../../data/model/PrefManager";
import LoanScopeDataHolder from "../../../data/model/LoanScopeDataHolder";
import { RolesConstants } from "../../../utils/RolesConstants";
import ConsentPhoneNumberVerifyDialog from "../../dialogs/PhoneNumberVerifyDialog";
import { SmsTypes } from "../../../utils/SmsTypesConstants";
import { MifosDateFormats } from "../../../utils/MifosDateFormats";
import DataTableUtils from "../../../utils/DataTableUtils";
import { ToolbarMenuItems } from "../../../utils/ToolbarMenuItems";
import LoanTasksUtility from "../../../utils/LoanTasksUtility";
import AttributeUtil, { AttributeDetails } from "../../../utils/AttributeUtil";
import { EntityType } from "../../../utils/EntityType";
import { Constants } from '../../Constants';
import UpdateIdentifierVerifyDialog from "@/components/dialogs/UpdateIdentifierVerifyDialog";
import UpdateIdentifierMessageDialog from "@/components/dialogs/SingleMessageDialog";
import { ValidationPatterns } from "@/utils/ValidationPatterns";

const MALE = "MALE";
const FEMALE = "FEMALE";
const OTHER = "OTHER";
const MALE_VALUE = "Male";
const FEMALE_VALUE = "Female";
const OTHER_VALUE = "Other";
const INVALID_DATE = "Invalid date";
const MAX_NSDL_RETRY = 2;
const LOCALE_EN = "en";

const MINIMUM_ALLOWED_AGE = 18 * 365 * 24 * 60 * 60 * 1000; //Eighteen years

export default {
  name: "ClientDetailsActivity",
  components: {
    ErrorAlertItem,
    ToolBar,
    WxcButton,
    DatePickerDialog,
    WxcRadio,
    WxcDropdown,
    Loading,
    ChooseClientIdentifierDocumentItem,
    ConsentPhoneNumberVerifyDialog,
    UpdateIdentifierVerifyDialog,
    UpdateIdentifierMessageDialog
  },

  data: () => ({
    title: "Client Details",
    consentFetched: false,
    showClientIdentifierOptions: true,
    firstStage: true,
    nsdlRetryFailure: 0,
    consentCheckedListSize: 0,
    consentHeading: "",
    consents: [],
    consentOtpRequired: true,
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    defaultSelectedDate: "1995-01-01",
    showDatePicker: false,
    showClientIdentifierCheckDialog: false,
    clientIdentifierCheckResponse: {},
    showClientDetails: false,
    showConsentPhoneNumberVerifyDialog: false,
    clientIdentifierDocumentName: "",
    updateIdentifierMessage: "",
    showUpdateIdentifierVerifyDialog: false,
    showUpdateIdentifierMessageDialog: false,

    //data for loan creation
    clientIdentifierDocumentType: "",
    clientIdentifierDocumentId: "",
    mobileNumber: "",
    personFullName: "",
    dob: "",
    gender: "",
    officeIdInLead: -1,
    leadId: null,
    city: null, // city from lead data
    officeExternalId: null,
    partnerName: null,
    verificationDOB: null, //TO verify client in case of update PhoneNumber or identifier
  }),

  methods: {
    checkBoxLink(consent) {
      if (consent.checked) {
        return ImageResourcesUtil.getImageLink(
            ImageResourcesUtil.CHECKED_CHECKBOX
        );
      } else {
        return ImageResourcesUtil.getImageLink(
            ImageResourcesUtil.UNCHECKED_CHECKBOX
        );
      }
    },

    onConsentSelected(consent) {
      consent.checked = !consent.checked;
      if (consent.checked) {
        this.consentCheckedListSize += 1;
      } else {
        this.consentCheckedListSize -= 1;
      }
    },

    genderSelected(e) {
      this.setGenderValueIfValidGender(e.value);
    },

    setGenderValueIfValidGender(gender) {
      if (this.isValidGender(gender)) {
        this.gender = EzUtils.convertToUpperCamelCase(gender);
      }
    },

    nameEntered(name) {
      this.personFullName = EzUtils.convertToUpperCamelCase(name);
    },

    onClientIdentifierDocumentIdEntered(clientIdentifierDocumentId) {
      this.clientIdentifierDocumentId = clientIdentifierDocumentId.toUpperCase();
      if (!this.firstStage) {
        this.showFirstStage();
      }
    },

    showFirstStage() {
      this.resetConsent();
      this.firstStage = true;
    },

    resetConsent() {
      this.consentCheckedListSize = 0;
      this.consents && this.consents.forEach(consent => {
        consent.checked = false;
      });
    },

    contactSupport() {
    },

    fetchConsents() {
      this.loading = true;
      const preferredRole = LoanScopeDataHolder.isOnboardingLoan() ? RolesConstants.RETAILER : "";
      LoanTasksUtility.getConsentDetails(this.getOfficeId, preferredRole, this.officeExternalId)
      .then(consentDetailsRes => {
        this.loading = false;
        if (consentDetailsRes.errorOccurred) {
          this.showError(consentDetailsRes.data);
        } else {
          this.consentFetched = true;
          this.consentHeading = consentDetailsRes.data.consentHeader;
          this.consentOtpRequired = consentDetailsRes.data.otpRequired;
          const consentList =
              consentDetailsRes.data && consentDetailsRes.data.consentList
                  ? consentDetailsRes.data.consentList
                  : [];
          consentList &&
          consentList.forEach(consent => {
            this.consents.push({ consentValue: consent, checked: false });
          });
        }
      });
    },

    onClientIdentifierDocumentSelected() {
      this.showClientIdentifierOptions = false;
    },

    onEnterDetailsManually(clientIdentifierDocumentType, panOCRData) {
      this.showClientIdentifierOptions = false;
      this.clientIdentifierDocumentType = clientIdentifierDocumentType;
      this.clientIdentifierDocumentName = EzUtils.removeUnderscoreFromString(
          clientIdentifierDocumentType, ["PAN", "ID"]
      );
      if (this.isClientIdentifierDocumentPan) {
        if (panOCRData) {
          this.clientIdentifierDocumentId = panOCRData.pan_number;
        }

        //If lead data exist fetch from there
        if (LoanScopeDataHolder.getLeadDetails()
            && LoanScopeDataHolder.getLeadDetails().data
            && !this.clientIdentifierDocumentId
        ) {
          this.clientIdentifierDocumentId = EzUtils.convertToJSONObjectIfString(
              LoanScopeDataHolder.getLeadDetails().data
          ).panNumber;
        }
      }
    },

    showDatePickerDialog() {
      this.showDatePicker = true;
      this.$refs.dob.blur();
    },

    dobSelected(date) {
      this.showDatePicker = false;
      if (this.$refs && this.$refs.dob) {
        this.$refs.dob.blur();
      }
      this.dobSelectedWithDateFormat(date, DateFormats.YYYY_MM_DD);
    },

    dobSelectedWithDateFormat(date, dateFormat) {
      const formattedDOB = DateUtils.getFormatDateAsString(
          date,
          dateFormat,
          DateFormats.DD_MMMM_YYYY
      );
      const defaultSelectedDate = DateUtils.getFormatDateAsString(
          date,
          dateFormat,
          DateFormats.YYYY_MM_DD
      );
      if (formattedDOB
          && defaultSelectedDate
          && !EzUtils.areEqualString(INVALID_DATE, formattedDOB, true)
          && !EzUtils.areEqualString(INVALID_DATE, defaultSelectedDate, true)
      ) {
        this.defaultSelectedDate = defaultSelectedDate;
        this.dob = formattedDOB;
      }
    },

    checkDedupe() {
      LoanScopeDataHolder.setClientId(-1);
      if (this.checkForDedupeValidation()) {
        this.verifyClientIdentifier();
      }
    },

    checkForDedupeValidation() {
      if (!this.isClientIdentifierIdValid()) {
        this.showToast(`Please enter valid ${this.clientIdentifierDocumentName}`);
        return false;
      }

      if (this.consents.length > this.consentCheckedListSize) {
        this.showToast(MessageConstants.TERMS_AND_CONDITION_MESSAGE);
        return false;
      }

      return true;
    },

    verifyClientIdentifier() {
      this.loading = true;
      DataSource.verifyClientIdentifiers({
        identifierType: Constants.getIdentifierTypeByIdentifierDocument(
            this.clientIdentifierDocumentType),
        identifierId: this.clientIdentifierDocumentId, // todo convert pan to identifier id
        mobile: this.mobileNumber,
        officeId: this.getOfficeId
      })
      .then(clientIdentifierCheckResponseData => {
        this.loading = false;
        if (clientIdentifierCheckResponseData.errorOccurred) {
          this.showError(clientIdentifierCheckResponseData.data);
        } else {
          if (clientIdentifierCheckResponseData.data.success) {
            this.firstStage = false;
            const clientId = clientIdentifierCheckResponseData.data.clientId;
            if (EzUtils.isValidId(clientId)) {
              LoanScopeDataHolder.setClientId(clientId);
              this.onClientExist();
            }
          } else {
            const clientIdentifierCheckResponse = clientIdentifierCheckResponseData.data;
            const allowUpdateClientDetails = clientIdentifierCheckResponse.allowUpdateClientDetails;
            const updateIdentifierMessage = clientIdentifierCheckResponse.userMessageDescription
                ? clientIdentifierCheckResponse.userMessageDescription
                : clientIdentifierCheckResponse.userMessage;

            const dateOfBirthRequired = clientIdentifierCheckResponse.dateOfBirthRequired;

            if (allowUpdateClientDetails) {
              this.showUpdateIdentifierVerifyDialog = true;
              this.$refs.updateIdentifierVerifyDialog.verifyPhoneNumber(
                  this.mobileNumber,
                  SmsTypes.MOBILE_VERIFICATION,
                  updateIdentifierMessage,
                  dateOfBirthRequired
              );
            } else {
              this.updateIdentifierMessage = updateIdentifierMessage;
              this.showUpdateIdentifierMessageDialog = true;
            }
          }
        }
      });
    },

    onUpdateIdentifierDataEntered(updateIdentifierData) {
      const updateClientMobileNumberRequest = {
        identifierType: Constants.getIdentifierTypeByIdentifierDocument(
            this.clientIdentifierDocumentType
        ),
        identifierId: this.clientIdentifierDocumentId,
        mobile: this.mobileNumber,
        dateOfBirth: updateIdentifierData.dateOfBirth ? updateIdentifierData.dateOfBirth : "",
        officeId: this.getOfficeId,
        otp: updateIdentifierData.verifiedOTP,
        otpId: updateIdentifierData.verifiedOTPId
      };

      this.loading = true;

      DataSource.updateClientMobileNumber(updateClientMobileNumberRequest)
      .then(updateClientMobileNumberRes => {
        this.loading = false;
        const updateClientMobileNumberData = updateClientMobileNumberRes.data;
        if (updateClientMobileNumberRes.errorOccurred) {
          this.showError(updateClientMobileNumberData);
        } else {
          if (updateClientMobileNumberData.success) {
            this.showUpdateIdentifierVerifyDialog = false;
            this.showUpdateIdentifierMessageDialog = true;
            this.updateIdentifierMessage = updateClientMobileNumberData.userMessage
                ? updateClientMobileNumberData.userMessage
                : "User details updated. Please continue.";
          } else {
            this.updateIdentifierMessage = updateClientMobileNumberData.userMessage;
            this.showUpdateIdentifierMessageDialog = true;
          }
        }
      })
    },

    onClientExist() {
      this.loading = true;
      DataSource.getClient(LoanScopeDataHolder.getClientId())
      .then(clientRes => {
        this.loading = false;
        if (!clientRes.errorOccurred) {
          const clientData = clientRes.data;
          this.loading = false;
          if (clientData.displayName) {
            this.nameEntered(clientData.displayName);
          }
          if (clientData.gender && this.isValidGender(clientData.gender.name)) {
            this.setGenderValueIfValidGender(clientData.gender.name);
          }
          const dateObj = clientData.dateOfBirth || clientData.dobDate;
          if (dateObj && dateObj.length === 3) {
            this.dobSelected(EzUtils.join("-", dateObj[0], dateObj[1], dateObj[2]));
          }
        }
      })
    },

    onNext() {
      if (LoanScopeDataHolder.getLoanAccountNumber()) {
        this.afterLoanCreation();
      } else {
        if (this.checkForValidation()) {
          if (this.isClientIdentifierDocumentPan) {
            this.verifyPan();
          } else {
            this.verifyCustomerPhoneNumberIfNeeded();
          }
        }
      }
    },

    verifyPan() {
      this.loading = true;
      DataSource.verifyPan({
        panNumber: this.clientIdentifierDocumentId.toUpperCase(),
        name: this.personFullName
      })
      .then(panVerificationResponseData => {
        this.loading = false;
        if (panVerificationResponseData.errorOccurred) {
          this.showError(panVerificationResponseData.data);
        } else {
          if (panVerificationResponseData.data.match) {
            this.onPanDataMatch();
          } else {
            this.onPanDataMismatch(panVerificationResponseData.data.panValid);
          }
        }
      });
    },

    onPanDataMatch() {
      this.verifyCustomerPhoneNumberIfNeeded();
    },

    onPanDataMismatch(validPan) {
      if (validPan) {
        if (++this.nsdlRetryFailure > MAX_NSDL_RETRY) {
          this.verifyCustomerPhoneNumberIfNeeded();
        } else {
          this.showToast(MessageConstants.CHECK_IF_NAME_AND_PAN_CORRECT);
        }
      } else {
        this.showToast(MessageConstants.PAN_IS_NOT_VALID);
        this.showFirstStage();
      }
    },

    verifyCustomerPhoneNumberIfNeeded() {
      if (this.consentOtpRequired && !LoanScopeDataHolder.isOnboardingLoan()) {
        this.showConsentPhoneNumberVerifyDialog = true;
        this.$refs.consentPhoneNumberVerifyDialog.verifyPhoneNumber(
            this.mobileNumber,
            SmsTypes.LOAN_CONSENT_CUSTOMER_AGENT,
            "Please verify the Phone Number of the Customer",
            this.getOfficeIdForLoanCreation,
            this.officeExternalId
        );
      } else {
        this.onConsentPhoneNumberVerified(
            { phoneNumber: this.mobileNumber, verifiedOTP: "NA" }
        )
      }
    },

    onConsentPhoneNumberVerified(verifiedData) {
      this.showConsentPhoneNumberVerifyDialog = false;
      if (!verifiedData || verifiedData.phoneNumber !== this.mobileNumber) {
        this.showToast("Something went wrong. Please re-apply a new loan.");
        ActivityNavigator.navigateTo(Activities.LOAN_DETAILS);
        return;
      }
      LoanScopeDataHolder.setConsentAccepted(true);
      LoanScopeDataHolder.setConsentAcceptedOTP(verifiedData.verifiedOTP);
      LoanScopeDataHolder.setConsentAcceptedTimestamp(
          DateUtils.getCurrentTimeAsFormat(DateFormats.YYYY_MM_DD__HH_mm_ss_SSS)
      );
      this.loading = true;
      DataSource.getIpAddress()
      .then(ipAddressRes => {
        this.loading = false;
        if (!ipAddressRes.errorOccurred && ipAddressRes.data) {
          const ip = ipAddressRes.data.ip ? ipAddressRes.data.ip : "";
          LoanScopeDataHolder.setConsentAcceptedIPAddress(ip);
        }

        this.updateClientIfNeeded();
      });
    },

    updateClientIfNeeded() {
      if (LoanScopeDataHolder.doesClientExist()) {
        this.loading = true;
        DataSource.getClientTemplate()
        .then(clientTemplateRes => {
          if (clientTemplateRes.errorOccurred) {
            this.showError(clientTemplateRes.data);
          } else {
            const clientTemplate = clientTemplateRes.data;
            const clientPayload = {}
            clientPayload.locale = LOCALE_EN;
            clientPayload.dateFormat = MifosDateFormats.dd_MMMM_yyyy;
            const names = EzUtils.parseFullName(this.personFullName);
            if (names && names.length === 3) {
              clientPayload.firstname = names[0];
              clientPayload.middlename = names[1];
              clientPayload.lastname = names[2];
            }
            clientPayload.dateOfBirth = this.dob;
            if (clientTemplate) {
              clientPayload.genderId =
                  DataTableUtils.getOptionId(clientTemplate.genderOptions, this.gender);
            }

            DataSource.updateClient(LoanScopeDataHolder.getClientId(), clientPayload)
            .then(updateClientResponse => {
              this.loading = false;
              if (updateClientResponse.errorOccurred) {
                this.showError(updateClientResponse.errorOccurred);
              } else {
                this.onClientUpdated();
              }
            })
          }
        })
      } else {
        this.createLoan();
      }
    },

    onClientUpdated() {
      this.createLoan();
    },

    createLoan() {
      const parsedName = EzUtils.parseFullName(this.personFullName);
      const identifierType =
          Constants.getIdentifierTypeByIdentifierDocument(this.clientIdentifierDocumentType);
      let applicantType = LoanScopeDataHolder.getApplicantType();
      if (!applicantType) {
        applicantType = Constants.APPLICANT_TYPE_INDIVIDUAL;
      }
      let companyType = null;
      if (LoanScopeDataHolder.getCompanyType()
          && !EzUtils.areEqualString(
              LoanScopeDataHolder.getCompanyType(),
              Constants.COMPANY_TYPE_INDIVIDUAL
          )
      ) {
        companyType = LoanScopeDataHolder.getCompanyType();
      }
      const customerDetails = {
        firstName: parsedName[0],
        middleName: parsedName[1],
        lastName: parsedName[2],
        gender: this.gender.toUpperCase(),
        dateOfBirth: this.dob,
        mobileNumber: this.mobileNumber,
        city: this.city,
        officeId: this.getOfficeIdForLoanCreation,
        entityType: applicantType,
        companyType: companyType,
        identifierData: {
          identifierType: identifierType,
          companyType: companyType
        }
      };

      if (this.clientIdentifierDocumentId) {
        customerDetails.identifierData.identifierId = this.clientIdentifierDocumentId.toUpperCase();
      }

      if (this.isClientIdentifierDocumentPan) {
        customerDetails.panNumber = this.clientIdentifierDocumentId.toUpperCase();
      }

      const loanCreationPayload = { customerDetails: customerDetails };
      if (EzUtils.isValidId(this.leadId)) {
        loanCreationPayload.leadId = this.leadId;
      }
      const staffId = this.isAgent ? PrefManager.getStaffId() : -1;
      if (EzUtils.isValidId(staffId)) {
        loanCreationPayload.staffId = staffId;
      }
      if (this.partnerName) {
        loanCreationPayload.partnerName = this.partnerName;
      }
      if (this.officeExternalId) {
        loanCreationPayload.officeExternalId = this.officeExternalId;
      }

      loanCreationPayload.loanAmount = LoanScopeDataHolder.getLoanAmount();
      loanCreationPayload.productCategory = LoanScopeDataHolder.getProductCategory();
      loanCreationPayload.productBrand = LoanScopeDataHolder.getBrand();
      loanCreationPayload.productModel = LoanScopeDataHolder.getModel();

      if (EzUtils.isValidId(LoanScopeDataHolder.getParentLoanId())) {
        loanCreationPayload.associatedEntityType = Constants.getRawValueForAssociatedEntityType(
            LoanScopeDataHolder.getAssociatedEntityType()
        );
        loanCreationPayload.parentLoanId = LoanScopeDataHolder.getParentLoanId();
        loanCreationPayload.relationWithApplicant = LoanScopeDataHolder.getRelationWithApplicant();
      }

      this.loading = true;
      DataSource.createLoanOnCreditEngine(loanCreationPayload)
      .then(createLoanRes => {
        this.loading = false;
        if (createLoanRes.errorOccurred) {
          this.showError(createLoanRes.data);
        } else {
          LoanScopeDataHolder.setLoanAccountNumber(createLoanRes.data.loanAccountNumber);
          LoanScopeDataHolder.setLoanId(createLoanRes.data.loanId);
          this.afterLoanCreation();
        }
      })
    },

    afterLoanCreation() {
      this.loading = true;
      DataSource.createOrUpdateAttribute(this.getConsentAttributeData())
      .then(attributeRes => {
        this.loading = false;
        if (attributeRes.errorOccurred) {
          this.showError(attributeRes.data);
        } else {
          LoanScopeDataHolder.setFetchingLoanDetailsFirstTime(true);
          LoanTasksActivityNavigator.nextActivity(Activities.WAIT);
        }
      });
    },

    getConsentAttributeData() {
      const consentAttributes = [];
      if (LoanScopeDataHolder.isConsentAccepted()) {
        consentAttributes.push(AttributeUtil.getSingleAttributeData(
            AttributeDetails.CONSENT_ACCEPTED.key,
            AttributeDetails.CONSENT_ACCEPTED.name,
            "true"
        ));
        consentAttributes.push(AttributeUtil.getSingleAttributeData(
            AttributeDetails.CONSENT_ACCEPTED_OTP.key,
            AttributeDetails.CONSENT_ACCEPTED_OTP.name,
            LoanScopeDataHolder.getConsentAcceptedOTP()
        ));
        consentAttributes.push(AttributeUtil.getSingleAttributeData(
            AttributeDetails.CONSENT_ACCEPTED_TIMESTAMP.key,
            AttributeDetails.CONSENT_ACCEPTED_TIMESTAMP.name,
            LoanScopeDataHolder.getConsentAcceptedTimestamp()
        ));
        consentAttributes.push(AttributeUtil.getSingleAttributeData(
            AttributeDetails.CONSENT_ACCEPTED_IP_ADDRESS.key,
            AttributeDetails.CONSENT_ACCEPTED_IP_ADDRESS.name,
            LoanScopeDataHolder.getConsentAcceptedIPAddress()
        ));
      } else {
        consentAttributes.push(AttributeUtil.getSingleAttributeData(
            AttributeDetails.CONSENT_ACCEPTED.key,
            AttributeDetails.CONSENT_ACCEPTED.name,
            "false"
        ));
      }

      return AttributeUtil.getAttributeUpdateDtoFromList(
          EntityType.LOANS,
          LoanScopeDataHolder.getLoanId(),
          consentAttributes
      );
    },

    checkForValidation() {
      if (!this.isClientIdentifierIdValid()) {
        this.showToast(`Please enter valid ${this.clientIdentifierDocumentName}`);
        return false;
      }

      if (!EzUtils.validatePersonName(this.personFullName)) {
        this.showToast(MessageConstants.EMPTY_NAME_MESSAGE);
        return false;
      }
      if (EzUtils.isEmpty(this.dob)) {
        this.showToast(`Enter valid ${this.getLabelForDateOfBirth}`);
        return false;
      }

      if (!this.isApplicantOrganization) {
        if (new Date().getTime() - new Date(this.dob).getTime() < MINIMUM_ALLOWED_AGE) {
          this.showToast(MessageConstants.MINIMUM_AGE_MESSAGE);
          return false;
        }

        if (!this.isValidGender(this.gender) && !this.isOnboardingLoan) {
          this.showToast(MessageConstants.SELECT_GENDER_MESSAGE);
          return false;
        }
      }

      return true;
    },

    isClientIdentifierIdValid() {
      if (this.isClientIdentifierMandatory) {
        const pattern = this.getClientIdentifierPattern();
        if (pattern) {
          if (!EzUtils.doesPatternMatch(pattern, this.clientIdentifierDocumentId)) {
            return false;
          }
        } else {
          if (EzUtils.isEmpty(this.clientIdentifierDocumentId)) {
            return false;
          }
        }
      }

      return true;
    },

    getClientIdentifierPattern() {
      let pattern = "";
      if (this.isClientIdentifierDocumentPan) {
        if (EzUtils.areEqualString(
            Constants.APPLICANT_TYPE_ORGANIZATION,
            LoanScopeDataHolder.getApplicantType(), true
        )) {
          pattern = ValidationPatterns.NON_INDIVIDUAL_PAN_PATTERN;
        } else {
          pattern = ValidationPatterns.INDIVIDUAL_PAN_PATTERN;
        }
      } else if (this.isClientIdentifierDocumentGst) {
        pattern = ValidationPatterns.GST_PATTERN;
      }

      return pattern;
    },

    isValidGender(gender) {
      return (
          EzUtils.areEqualString(gender, MALE, true) ||
          EzUtils.areEqualString(gender, FEMALE, true) ||
          EzUtils.areEqualString(gender, OTHER, true)
      );
    },

    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    showToast(message) {
      AlertService.showToast(message);
    },

    setLoanCreationDetailsUsingLeadData(leadDetails) {
      if (leadDetails) {
        if (leadDetails.id) {
          this.leadId = leadDetails.id;
        }
        if (leadDetails.partner_name) {
          this.partnerName = leadDetails.partner_name;
        }
        if (leadDetails.office_id) {
          this.officeIdInLead = leadDetails.office_id;
        }
      }

      const leadDetailsData = leadDetails && leadDetails.data
          ? EzUtils.convertToJSONObjectIfString(leadDetails.data)
          : {};

      if (leadDetailsData) {
        this.city = leadDetailsData.city;
        if (!(LoanScopeDataHolder.fillClientDetailsWithLeadData() === false)) {
          if (this.isClientIdentifierDocumentPan && EzUtils.validatePan(
              leadDetailsData.panNumber)) {
            this.clientIdentifierDocumentId = leadDetailsData.panNumber.toUpperCase();
          }
          this.setGenderValueIfValidGender(leadDetailsData.gender);
          this.nameEntered(EzUtils.mergeName(
              leadDetailsData.firstName,
              leadDetailsData.middleName,
              leadDetailsData.lastName
          ));

          if (leadDetailsData.dateOfBirth) {
            this.dobSelectedWithDateFormat(leadDetailsData.dateOfBirth, DateFormats.DD_MMMM_YYYY);
          }
        }
      }
    }
  },

  created: function () {
    if (LoanScopeDataHolder.getLoanAccountNumber()) {
      LoanTasksActivityNavigator.nextActivity(Activities.LOAN_DETAILS);
    } else {
      if (this.isAgent) {
        this.mobileNumber = LoanScopeDataHolder.getMobile();
        if (LoanScopeDataHolder.getLeadDetails()) {
          this.setLoanCreationDetailsUsingLeadData(LoanScopeDataHolder.getLeadDetails());
        }
      } else {
        this.mobileNumber = PrefManager.getUserMobile();
        if (LoanScopeDataHolder.getLeadDetails()) {
          this.setLoanCreationDetailsUsingLeadData(LoanScopeDataHolder.getLeadDetails());
        } else if (LoanScopeDataHolder.getMerchantID()) {
          /*Loan using Merchant id*/
          this.officeExternalId = LoanScopeDataHolder.getMerchantID();
        }
      }
    }
  },

  mounted() {
    this.showClientDetails = true;
    this.fetchConsents();
  },

  computed: {
    toolbarTitle() {
      return LoanScopeDataHolder.isOnboardingLoan() ? "Merchant Details" : "Client Details";
    },

    checkBoxConfig() {
      return DefaultResourceConfig.CHECK_BOX;
    },

    radioButtonConfig() {
      return DefaultResourceConfig.RADIO_BUTTON;
    },

    genderList() {
      let genderList = [
        { title: MALE, value: MALE_VALUE },
        { title: FEMALE, value: FEMALE_VALUE },
        { title: OTHER, value: OTHER_VALUE }
      ];
      if (EzUtils.areEqualString(MALE_VALUE, this.gender, true)) {
        genderList[0].checked = true;
      } else if (EzUtils.areEqualString(FEMALE_VALUE, this.gender, true)) {
        genderList[1].checked = true;
      } else if (EzUtils.areEqualString(OTHER_VALUE, this.gender, true)) {
        genderList[2].checked = true;
      }
      return genderList;
    },

    closeActionNavigationActivity() {
      return Activities.HOME;
    },

    toolbarActionsList() {
      return [
        ToolbarMenuItems.CONTACT_SUPPORT,
        ToolbarMenuItems.LOGOUT,
        ToolbarMenuItems.CLOSE
      ];
    },

    getOfficeIdForLoanCreation() {
      if (this.isAgent) {
        return PrefManager.getUserOfficeId();
      }
      if (LoanScopeDataHolder.getMerchantID()) {
        return -1;
      }
      return this.getOfficeId;
    },

    getOfficeId() {
      if (this.isAgent) {
        return PrefManager.getUserOfficeId();
      }
      if (EzUtils.isValidId(this.officeIdInLead)) {
        return this.officeIdInLead;
      }
      return PrefManager.getUserOfficeId();
    },

    isAgent() {
      return PrefManager.doesUserHasRole(RolesConstants.AGENT);
    },

    currentDateAsYYYYMMDD() {
      return DateUtils.getCurrentTimeAsFormat(DateFormats.YYYY_MM_DD);
    },

    isApplicantOrganization() {
      return Constants.APPLICANT_TYPE_ORGANIZATION === LoanScopeDataHolder.getApplicantType();
    },

    getLabelForDateOfBirth() {
      return this.isApplicantOrganization || this.isOnboardingLoan
          ? "Date of Incorporation" : "Date of Birth";
    },

    isOnboardingLoan() {
      return LoanScopeDataHolder.isOnboardingLoan();
    },

    getIdentifierIdLabel() {
      return `Please enter your ${this.clientIdentifierDocumentName}`;
    },

    isClientIdentifierMandatory() {
      return !this.isClientIdentifierDocumentForm60;
    },

    isClientIdentifierDocumentForm60() {
      return EzUtils.areEqualString(
              Constants.DOCUMENT_TYPE_FORM_60, this.clientIdentifierDocumentType, true
          )
          || EzUtils.areEqualString(
              Constants.IDENTIFIER_FORM_60, this.clientIdentifierDocumentType, true
          )
    },

    isClientIdentifierDocumentGst() {
      return EzUtils.areEqualString(
              Constants.DOCUMENT_TYPE_GST, this.clientIdentifierDocumentType, true
          )
          || EzUtils.areEqualString(
              Constants.IDENTIFIER_GST_NUMBER, this.clientIdentifierDocumentType, true
          )
    },

    isClientIdentifierDocumentPan() {
      return EzUtils.areEqualString(
              Constants.DOCUMENT_TYPE_PAN, this.clientIdentifierDocumentType, true
          )
          || EzUtils.areEqualString(
              Constants.IDENTIFIER_PAN_CARD, this.clientIdentifierDocumentType, true
          );
    },

    getClientIdentifierInputMaxLength() {
      let maxLength = 100;
      if (this.isClientIdentifierMandatory) {
        if (this.isClientIdentifierDocumentPan) {
          maxLength = 10;
        }
      }

      return maxLength;
    }
  }
};
</script>

<style scoped src="../../../assets/style/base.css"></style>
